import React, { useEffect, useState } from "react";
import { AtSign, Calendar, ChevronLeft, ChevronRight, Clock, Mail, MapPin, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PROFILE_FIELDS } from "../lib/profilePresets";

const SERVICE_TYPES = [
  { v: "escort", l: "Escort / Companion" },
  { v: "massage", l: "Erotic Massage" },
  { v: "domination", l: "Domination" },
  { v: "submissive", l: "Submissive" },
  { v: "virtual", l: "Virtual / Cam" },
  { v: "show", l: "Live Show / Event" },
  { v: "other", l: "Other (specify in notes)" },
];
const DURATIONS = ["1h", "2h", "3h", "overnight", "weekend", "custom"];
const VISIT_KINDS = [
  { v: "incall", l: "Incall (you come to me)" },
  { v: "outcall", l: "Outcall (I come to you)" },
  { v: "virtual", l: "Virtual / online" },
];

export default function BookMeExperience() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api.get("/api/public/landing")
      .then(({ data }) => { if (active) setConfig(data || {}); })
      .catch((error) => {
        if (active) toast.error(error?.response?.data?.detail || "Book Me is unavailable right now");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  if (loading) return <div className="py-16 text-center text-sm text-zinc-400" data-testid="members-book-me-loading">Loading Book Me…</div>;
  if (!config) return <div className="py-16 text-center text-sm text-zinc-400" role="alert">Book Me could not be loaded.</div>;

  return (
    <section className="mx-auto max-w-3xl" data-testid="members-book-me">
      <header className="mb-6 border-b border-white/10 pb-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase text-purple-300"><Calendar size={14} /> Book Me</div>
        <h2 className="mt-2 text-2xl font-bold text-white">Request a session</h2>
        {config.escort_intro && <p className="mt-2 text-sm text-zinc-400">{config.escort_intro}</p>}
      </header>
      {(config.gallery_photos || []).length > 0 && <BookingGallery photos={config.gallery_photos} />}
      {config.profile_attrs && Object.values(config.profile_attrs).some(Boolean) && <ProfileAttrsGrid attrs={config.profile_attrs} />}
      <EscortBookingForm servicesOffered={config.services_offered || []} fetishesOffered={config.fetishes_offered || []} />
    </section>
  );
}

function ChipToggle({ label, selected, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={`rounded-full border px-2.5 py-1 text-xs font-bold uppercase tracking-wider transition ${selected ? "border-purple-500 bg-purple-500/15 text-purple-300" : "border-white/15 bg-white/5 text-zinc-400 hover:bg-white/10"}`}>
      {label}
    </button>
  );
}

function BookingGallery({ photos }) {
  const sorted = [...photos].sort((left, right) => (left.order || 0) - (right.order || 0));
  const [active, setActive] = useState(0);
  const [failedImageUrl, setFailedImageUrl] = useState(null);
  if (!sorted.length) return null;
  const activeIndex = active % sorted.length;
  const photo = sorted[activeIndex];
  const showPrevious = () => setActive((activeIndex - 1 + sorted.length) % sorted.length);
  const showNext = () => setActive((activeIndex + 1) % sorted.length);

  return (
    <div className="mb-5" data-testid="booking-gallery-carousel" aria-label="Booking photo gallery">
      <div className="relative flex aspect-[4/3] min-h-[240px] max-h-[55vh] items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-black">
        {photo?.url && failedImageUrl !== photo.url ? (
          <img key={photo.url} src={photo.url} alt={photo.caption || `Photo ${activeIndex + 1}`}
            data-testid="booking-gallery-active-photo" className="h-full w-full object-contain" loading="eager"
            onError={() => setFailedImageUrl(photo.url)} />
        ) : (
          <div className="px-6 text-center text-sm text-zinc-400" role="status">This photo could not be loaded.</div>
        )}
        {sorted.length > 1 && <>
          <button type="button" onClick={showPrevious} aria-label="Previous booking photo"
            className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/75 text-white shadow-lg hover:bg-black">
            <ChevronLeft size={22} />
          </button>
          <button type="button" onClick={showNext} aria-label="Next booking photo"
            className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/75 text-white shadow-lg hover:bg-black">
            <ChevronRight size={22} />
          </button>
        </>}
        <span className="absolute bottom-3 right-3 rounded bg-black/80 px-2 py-1 text-xs text-white" aria-live="polite">
          {activeIndex + 1} / {sorted.length}
        </span>
      </div>
      {photo?.caption && <p className="mt-2 text-sm text-zinc-300">{photo.caption}</p>}
      {sorted.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" aria-label="Choose a booking photo">
          {sorted.map((item, index) => (
            <button key={item.public_id || item.url || index} type="button" onClick={() => setActive(index)}
              aria-label={`Show booking photo ${index + 1}`} aria-pressed={index === activeIndex}
              data-testid={`booking-gallery-thumbnail-${index}`}
              className="h-16 w-16 shrink-0 overflow-hidden rounded border-2 bg-black sm:h-20 sm:w-20"
              style={{ borderColor: index === activeIndex ? "#a855f7" : "rgba(255,255,255,0.18)" }}>
              <img src={item.url} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileAttrsGrid({ attrs }) {
  const filled = PROFILE_FIELDS.filter(field => attrs[field.key]);
  if (!filled.length) return null;
  return (
    <div className="mb-5 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl">
      <div className="mb-3 text-xs font-bold uppercase tracking-wider text-zinc-400">▸ Profile details</div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-3">
        {filled.map(field => (
          <div key={field.key} className="flex justify-between gap-2 border-b border-white/5 py-1">
            <dt className="text-zinc-500">{field.label}</dt><dd className="text-right font-bold text-white">{attrs[field.key]}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function EscortBookingForm({ servicesOffered = [], fetishesOffered = [] }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", twitter_handle: "", service_type: "escort", duration: "1h",
    visit_kind: "incall", location: "", preferred_date: "", preferred_time: "", budget: "", interests: "",
    notes: "", selected_services: [], selected_fetishes: [],
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const toggleSelected = (key, value) => setForm(current => ({
    ...current,
    [key]: current[key].includes(value) ? current[key].filter(item => item !== value) : [...current[key], value],
  }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error("Please add your name");
    if (!form.email && !form.phone && !form.twitter_handle) return toast.error("Need at least one contact (email, phone or twitter)");
    setSending(true);
    try {
      await api.post("/api/public/escort-booking", form);
      setSent(true);
      toast.success("Booking request sent");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (sent) return (
    <div className="rounded-2xl border border-pink-500/30 bg-pink-500/10 p-8 text-center" data-testid="booking-request-success">
      <Calendar size={32} className="mx-auto mb-3 text-pink-400" />
      <div className="mb-1 text-xl font-bold text-white">Request received.</div>
      <div className="text-sm text-zinc-400">I review every booking personally. Expect a reply within 24h.</div>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-xl sm:p-6" data-testid="member-booking-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <PubInput placeholder="Your name *" value={form.name} onChange={value => setForm(current => ({ ...current, name: value }))} required icon={<AtSign size={13} />} />
        <PubInput placeholder="Email" type="email" value={form.email} onChange={value => setForm(current => ({ ...current, email: value }))} icon={<Mail size={13} />} />
        <PubInput placeholder="Phone (WhatsApp/Signal)" value={form.phone} onChange={value => setForm(current => ({ ...current, phone: value }))} icon={<Phone size={13} />} />
        <PubInput placeholder="@twitter" value={form.twitter_handle} onChange={value => setForm(current => ({ ...current, twitter_handle: value }))} icon={<AtSign size={13} />} />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <PubSelect label="Service" value={form.service_type} options={SERVICE_TYPES.map(item => ({ value: item.v, label: item.l }))} onChange={value => setForm(current => ({ ...current, service_type: value }))} />
        <PubSelect label="Duration" value={form.duration} options={DURATIONS.map(value => ({ value, label: value }))} onChange={value => setForm(current => ({ ...current, duration: value }))} />
        <PubSelect label="Type" value={form.visit_kind} options={VISIT_KINDS.map(item => ({ value: item.v, label: item.l }))} onChange={value => setForm(current => ({ ...current, visit_kind: value }))} />
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <PubInput placeholder="Location (city / hotel)" value={form.location} onChange={value => setForm(current => ({ ...current, location: value }))} icon={<MapPin size={13} />} />
        <PubInput placeholder="Preferred date" type="date" value={form.preferred_date} onChange={value => setForm(current => ({ ...current, preferred_date: value }))} />
        <PubInput placeholder="Preferred time" type="time" value={form.preferred_time} onChange={value => setForm(current => ({ ...current, preferred_time: value }))} icon={<Clock size={13} />} />
      </div>
      <PubInput placeholder="Budget (e.g. 500 EUR · 2h)" value={form.budget} onChange={value => setForm(current => ({ ...current, budget: value }))} />
      {servicesOffered.length > 0 && <ChoiceGroup title="Services you want (pick any)" values={servicesOffered} selected={form.selected_services} onToggle={value => toggleSelected("selected_services", value)} />}
      {fetishesOffered.length > 0 && <ChoiceGroup title="Kinks / fetishes you're into (pick any)" values={fetishesOffered} selected={form.selected_fetishes} onToggle={value => toggleSelected("selected_fetishes", value)} />}
      <PubTextarea placeholder="Kinks / vibe / fantasy you're after (anything not above)" rows={2} value={form.interests} onChange={value => setForm(current => ({ ...current, interests: value }))} />
      <PubTextarea placeholder="Anything else I should know — be specific, it helps me say yes" rows={3} value={form.notes} onChange={value => setForm(current => ({ ...current, notes: value }))} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-zinc-400">18+ only · Confidential · Every request is reviewed</p>
        <button type="submit" disabled={sending} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 px-6 py-2.5 text-sm font-bold uppercase text-white shadow-[0_0_20px_rgba(236,72,153,0.4)] transition hover:scale-105 disabled:opacity-50">
          <Send size={14} /> {sending ? "Sending…" : "Send booking"}
        </button>
      </div>
    </form>
  );
}

function ChoiceGroup({ title, values, selected, onToggle }) {
  return (
    <div>
      <div className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-400">{title}</div>
      <div className="flex flex-wrap gap-1.5">{values.map(value => <ChipToggle key={value} label={value} selected={selected.includes(value)} onClick={() => onToggle(value)} />)}</div>
    </div>
  );
}

export function PubInput({ placeholder, value, onChange, type = "text", testid, required, icon }) {
  return (
    <label className="relative block">
      {icon && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">{icon}</span>}
      <input type={type} value={value} onChange={event => onChange(event.target.value)} data-testid={testid}
        placeholder={placeholder} required={required} className={`w-full rounded-xl border border-white/10 bg-white/5 py-2.5 pr-3 text-sm text-white placeholder:text-zinc-500 focus:border-purple-500 focus:outline-none ${icon ? "pl-9" : "pl-3"}`} />
    </label>
  );
}

export function PubTextarea({ placeholder, value, onChange, rows = 3, testid }) {
  return <textarea value={value} onChange={event => onChange(event.target.value)} data-testid={testid} placeholder={placeholder} rows={rows}
    className="w-full resize-none rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-purple-500 focus:outline-none" />;
}

function PubSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-wider text-zinc-500">{label}</span>
      <select value={value} onChange={event => onChange(event.target.value)} data-testid={testid}
        className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white focus:border-purple-500 focus:outline-none">
        {options.map(option => <option key={option.value} value={option.value} style={{ background: "#000000" }}>{option.label}</option>)}
      </select>
    </label>
  );
}